import React from 'react';
import {createRoot} from "react-dom/client";
import LoadingIndicator from '../../common/loading_indicator.jsx';
import FormViewer from '../../form_editor/form_viewer.jsx';
import FormComments from "../../form_editor/form_comments.jsx";
import { useEffect } from 'react';
import LanguageSelector from '../../common/LanguageSelector.jsx';
import ConfirmDialog from "./ConfirmDialog.jsx";
import SaveSuccessDialog from "./SaveSuccessDialog.jsx";
import OptionalFormsButton from "./OptionalFormsButton.jsx";
import PdfAnchor from '../../common/pdf/PdfAnchor.jsx';
import { getTranslation } from "../../../helpers/index.js";
import CompareSubmissionsButton from "../edit/CompareSubmissionsButton.jsx"
import PrefillSourceSelectionDialog from "./PrefillSourceSelectionDialog.jsx"
import ProjectReportPdfExport from "../edit/Pdf.jsx"
import DiscussionButton from './DiscussionButton.jsx';

import {
  form_comment_get_path,
  programme_form_pdf_path,
  programme_form_save_answers_path,
  programme_form_download_attachment_path,
  programme_form_show_path
} from '~/routes';

import PdfButton from '../../common/pdf/ConfigurationCreator.jsx';
import FormViewerSkeleton from '../../../skeleton_loaders/FormViewerSkeleton.jsx';
import PaginationContext, { PaginationBoundary } from '../../form_editor/pagination_context.jsx';

const ErrorIndicator = ({warningLevel}) => <i 
  className={`alert-${warningLevel} fa fa-exclamation-triangle ml-1`} 
  title={I18n.t('active_validation_errors')} 
  style={{backgroundColor: 'transparent'}}
/>


const FormErrors = ({title, errors, warningLevel, className = ""}) => {
  const [open, setOpen] = React.useState(false);
  const highlightCalled = React.useRef(false);
  const handleOpen = () => {
    setOpen(!open);
  };

  useEffect(() => {
    if (open && !highlightCalled.current) {
      AdaliaForm.highlightErrors(".initialized");
      highlightCalled.current = true;
    }
  }, [open]);

  return (
    <ul className={`mt-1 alert alert-${warningLevel} list-unstyled form-errors ${className}`}>
      <li>
        <h3 className={`alert-${warningLevel}`}>{title}</h3>
      </li>
      <li onClick={handleOpen}>
        <b>{open ? I18n.t("hide_details") : I18n.t("click_here_for_details")}</b>
      </li>
      {Object.keys(errors).map((param) =>
        errors[param].map((err, idx) => (
          <li data-param={param} data-page-selector={err.page_id} data-highlight-selector={err.highlight_selector} key={param + ":" + idx} style={open ? {} : { display: "none" }}>
            {err.message}
          </li>
        ))
      )}
    </ul>
  );
};
export { FormErrors }

const FormWrapper = ({ editable, form, extraButtons, children, isSurvey, title }) => {
  // If no monitoring report title provided, form title and buttons not expected to be rendered in React
  if (!form.paged || isSurvey || !editable || !title) {
    return <>{children}</>
  }
  return (
    <div style={{display: 'flex', flexDirection: 'column'}}>
      <div className="col-xs-12 mb-2" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <h3 className="mt-1">{title}</h3>
        <div>{extraButtons}</div>
      </div>
      {children}
    </div>
  )
}

export default class FormCollection extends React.PureComponent {
  constructor(props) {
    super(props);

    // Set currentFormIndex with the first non-optional form index - Only in fill phase
    //  => selected_optional_form_ids: This attribute is only available in fill phase
    const currentActiveFormIndex = props.forms.findIndex(obj => obj.required);
    const currentFormIndex = (props.defaultActiveFormIndex > 0) ? props.defaultActiveFormIndex :
        props.hasOwnProperty('selected_optional_form_ids') && currentActiveFormIndex > -1 ? currentActiveFormIndex : props.activeFormIndex;

    this.formIds = { [currentFormIndex]: this.props.id };
    this.questions = { [currentFormIndex]: this.props.question_data };
    this.optionSetValues = { [currentFormIndex]: this.props.optionSetValues };
    this.optionSetHierarchyValues = { [currentFormIndex]: this.props.optionSetHierarchyValues };
    this.answers = this.readFormAnswersCache() || { [currentFormIndex]: this.props.answers };
    this.attachments = { [currentFormIndex]: this.props.attachments };
    this.comments = { [currentFormIndex]: this.props.comments };
    let loadedFormIndexes = [currentFormIndex];

    props.forms.forEach((f, i) => {
      this.formIds[i] = f.programme_form_id || f.id;
      this.questions[i] = this.questions[i] || f.question_data;
      this.answers[i] = this.answers[i] || f.form_data;
      this.attachments[i] = this.attachments[i] || f.attachments;
      this.optionSetValues[i] = this.optionSetValues[i] || {};
      this.optionSetHierarchyValues[i] = this.optionSetHierarchyValues[i] || {};

      if (this.questions[i] || f.hide_form) {
        loadedFormIndexes.push(i);
      }
    });

    this.state = {
      activeFormIndex: currentFormIndex,
      loadedFormIndexes,
      formSubmitting: false,
      language: props.language,
      disableSubmit: {},
      confirmation: {},
      savedSuccess: false,
      selectedOptionalFormIds: this.props.selected_optional_form_ids || [],
      forms: this.props.forms,
      isPreview: false,
      prefillAnswersFromMultiSource: null,
      showMultiSourceSelection: true,
      loading: true
    };

    // Always save if there's prefill
    this.unsavedChanges = !!Object.keys(props.prefill_answers || {}).length;
    this.formErrors = {};
    this.warningLevel = "warning";
    this.formRef = React.createRef();
    this.autoSavedChanges = {};
  }

  static get CONFIRMATION_STATUS() {
    return {
      PENDING: "PENDING",
      CONFIRMED: "CONFIRMED",
      CANCELED: "CANCELED",
    };
  }

  currentProgrammeFormLinkId = () =>
    this.props.forms[this.state.activeFormIndex].programme_form_link_id;

  handleSubmit = (e, extraSubmitData) => {
    e.preventDefault();

    if (typeof this.props.onSubmit == "function") {
      if (!this.props.onSubmit(extraSubmitData, this.submit)) {
        // Don't submit if onSubmit returns false
        return false;
      }
    }

    // Submit if onSubmit didn't return false
    this.submit(extraSubmitData);
  };

  submit = (extraSubmitData) => {
    const { asTabs } = this.props;
    this.setState({ formSubmitting: true });

    // Save all forms that have been changed as draft and call report submission.
    // Report submission will update the form statuses to submitted
    if (asTabs) {
      this.storeAnswers();
      Promise.all(
        Object.keys(this.autoSavedChanges).map((savePath) =>
          this.postForm(
            savePath.replace("__STATUS__", "draft").replace("__IS_SUBMIT__", true).replace("__DECISION__", typeof extraSubmitData === 'object' ? extraSubmitData.decision : undefined),
            this.autoSavedChanges[savePath]
          )
        )
      ).then((r) => {
        jQuery.post(this.props.submit_path, extraSubmitData || {}, (res) => {
          if (res.status == "ok") {
            if (res.redirect_back_to) {
              AdaliaForm.submitting = true;
              Turbo.visit(res.redirect_back_to);
            } else {
              AdaliaForm.updateOriginalState(this.formRef.current);

              const url = new URL(window.location.href);
              if (url.searchParams.get("edit") === "true") {
                url.searchParams.delete("edit");
              }
              Turbo.visit(url.toString(), {action: 'replace'});
            }
          } else {
            let index = 0;
            res.forEach((err) => {
              index = this.props.forms.findIndex(
                (f) => f.programme_form_link_id == err.programme_form_link_id
              );
              this.formErrors[index] = err.validation_errors;
            });
            window.scrollTo(0, 0);
            this.warningLevel = "danger";
            let activeFormIndex = index == -1 ? 0 : index;
            this.fetchQuestions(
              this.props.forms[activeFormIndex],
              activeFormIndex
            ).then((_) => {
              this.setState({
                formSubmitting: false,
                activeFormIndex: activeFormIndex,
                confirmation: {},
              });
              AdaliaForm.highlightErrors();
            });
          }
        }).fail((res) => {
          let errors = '';
          const modalBody = jQuery('.confirm-dialog .modal-body');
          if (res.responseJSON) {
            const wrapper = modalBody.find('#form_errors')[0] ?? Object.assign(document.createElement('div'), {id: 'form_errors'});
            const root = createRoot(wrapper);
            const error = res.responseJSON[this.currentProgrammeFormLinkId()] || res.responseJSON[0];
            root.render(<FormErrors errors={error.validation_errors} title={I18n.t('active_validation_errors')} warningLevel={'danger'}/>);
            errors = wrapper;
          } else {
            errors = `<div class="alert alert-danger">${res.responseText}</div>`;
          }
          modalBody.append(errors);
          this.setState({formSubmitting: false})
        });
      });
      // Else submit all (as they are visible)
    } else {
      this.storeAnswers();
      Promise.all(
        Object.keys(this.autoSavedChanges).map((savePath) => {
          return this.postForm(savePath.replace("__STATUS__", "submitted"), {
            ...this.autoSavedChanges[savePath],
            data: this.autoSavedChanges[savePath].data,
          });
        })
      ).then((r) => {
        var errors = false;
        for (var index in this.formErrors) {
          if (this.formErrors[index]) {
            errors = true;
          }
        }
        if (errors) {
          this.setState({ formSubmitting: false, confirmation: {} });
        } else {
          AdaliaForm.updateOriginalState(this.formRef.current);
          Turbo.visit(location.href, {action: 'replace'});
        }
      });
    }
  };

  confirmSubmit = (extraSubmitData) => {
    this.setState({
      confirmation: {
        status: this.constructor.CONFIRMATION_STATUS.PENDING,
        params: { ...(extraSubmitData || {}) },
      },
    });
  };

  postForm = (savePath, saveData) => {
    return new Promise((resolve, reject) => {
      this.updateFormAnswersCache(null);
      jQuery.ajax({
        url: savePath,
        data: saveData.data,
        type: "POST",
        dataType: "json",
        success: (data) => {
          this.answers[saveData.idx] = data.answers;
          this.attachments[saveData.idx] = data.attachments;
          this.formErrors[saveData.idx] = data.form_errors;
          resolve();
        },
      });
    });
  };

  handlePdfBtnClick = (cb) => {
    this.handleSave(null, cb)
  }

  handleSave = (e, cb) => {
    e?.preventDefault();
    this.setState({ formSubmitting: true });
    const saveButton = jQuery(e?.target);
    saveButton.text(I18n.t("saving"));

    // Save all forms that have been changed and update report status.
    this.storeAnswers();
    Promise.all(
      Object.keys(this.autoSavedChanges).map((savePath) =>
        this.postForm(
          savePath.replace("__STATUS__", "draft").replace("__IS_SUBMIT__", false).replace("__DECISION__", undefined),
          this.autoSavedChanges[savePath]
        )
      )
    ).then((r) => {
      const saveAndExit = $(e?.target).data('save-and-exit');

      // TODO: This is never defined, but probably unnecessary anyway?
      if (this.props.savePath) {
        //Filling phase
        jQuery.post(this.props.save_path, (res) => {
          if (!saveAndExit) {
            this.warningLevel = "warning";
            this.setState({ formSubmitting: false });
            saveButton.text(this.props.saveText);
          }
        });
      } else {
        //Reviewing phase, no save path.
        this.setState({ formSubmitting: false, savedSuccess: true });

        if (!saveAndExit) {
          saveButton.text(I18n.t("saved"));
          setTimeout(() => {
            saveButton.text(saveButton.data('original-text') || this.props.saveText);
          }, 2000);
        }

        if (typeof cb == "function") {
          cb();
        }
      }
      AdaliaForm.highlightErrors();
      AdaliaForm.updateOriginalState(this.formRef.current);
      this.autoSavedChanges = {};

      if (saveAndExit) {
        if (this.props.save_and_exit_path) {
          Turbo.visit(this.props.save_and_exit_path)
        } else {
          const url = new URL(window.location.href);
          url.searchParams.delete('edit');
          window.history.replaceState({}, document.title, url.toString());
          Turbo.visit(window.location.href)
        }
      }
    });
  };

  formViewerRendered = () => !!this.formRef.current?.querySelector('.form-viewer');

  // Force rich text editor to update latest content before saving
  syncRichTextEditorsBeforeSave = () => {
    if (typeof tinymce !== "undefined") {
      tinymce.triggerSave();
    }
  };

  storeAnswers = () => {
    if (this.unsavedChanges && this.formViewerRendered()) {
      const { formSavePathParams = {},
        formSaveUrl = programme_form_save_answers_path({
          programme_form_link_id: this.currentProgrammeFormLinkId(),
          entity_id: this.props.project_report_id,
          entity_type: "project_report",
          status: "__STATUS__",
          is_submit: "__IS_SUBMIT__",
          decision: "__DECISION__",
          ...formSavePathParams
        }) } = this.props;

      this.syncRichTextEditorsBeforeSave();

      this.autoSavedChanges[
        formSaveUrl.replace(
          '__programme_form_link_id__',
          this.currentProgrammeFormLinkId()
        )
      ] = { idx: this.state.activeFormIndex, data: jQuery(this.formRef.current).serialize() };
    }
  };

  // If the form content has changed, save the answers.
  // FormData is formatted  by sending it to the backend and using rails functionality
  saveAnswersIfChanged = () => {
    return new Promise((resolve, reject) => {
      if (this.unsavedChanges && this.formViewerRendered() && this.props.editable && !this.props.forms[this.state.activeFormIndex].disable_edit) {
        this.storeAnswers();

        const { formSavePathParams = {},
          formSaveUrl = programme_form_save_answers_path({
            programme_form_link_id: this.currentProgrammeFormLinkId(),
            entity_id: this.props.project_report_id,
            entity_type: "project_report",
            status: "autosave",
            ...formSavePathParams
          }) } = this.props;

        jQuery.ajax({
          url: formSaveUrl.replace(
            '__programme_form_link_id__',
            this.currentProgrammeFormLinkId()
          ),
          data: jQuery(this.formRef.current).serialize(),
          type: "POST",
          dataType: "json",
          success: (data) => {
            this.answers[this.state.activeFormIndex] = data.answers;
            this.attachments[this.state.activeFormIndex] = data.attachments;
            this.formErrors[this.state.activeFormIndex] = data.form_errors;
            resolve();
          },
        });
      } else {
        resolve();
      }
    });
  };

  // Fetch question data, if not already fetched
  fetchQuestions = (form, index) => {
    const { programme_form_link_id } = form;
    const { commentPermissions, project_report_id, project_report_version } = this.props;

    const promises = [];

    // Promises for fetching question data, answers, and attachments
    if (!this.questions[index] || (this.props.editable && this.props.forms[index].disable_edit)) {
      const { formPathParams = {},
          formUrl = programme_form_show_path({
            format: "json",
            programme_form_link_id: programme_form_link_id,
            entity_id: this.props.project_report_id,
            entity_type: "project_report",
            project_report_version: project_report_version,
            ...formPathParams,
          }) } = this.props;

      const questionDataPromise = new Promise((resolve, reject) => {
        jQuery.ajax({
          method: "GET",
          url: formUrl.replace(
            "__programme_form_link_id__",
            programme_form_link_id
          ),
          success: (data) => {
            this.formIds[index] ||= data.id;
            this.questions[index] = data.question_data;
            this.optionSetValues[index] = data.option_set_values;
            this.optionSetHierarchyValues[index] = data.option_set_hierarchy_values;
            this.answers[index] = this.props.forms[index].disable_edit ? data.answers : this.answers[index] || data.answers;
            this.attachments[index] = data.attachments;
            resolve();
          },
          error: (jqXHR, textStatus, errorThrown) => {
            pdfDebug(textStatus);
            pdfDebug(errorThrown);
            reject(new Error(`Failed to fetch questions: ${textStatus} - ${errorThrown}`));
          },
        });
      });
      promises.push(questionDataPromise);
    } else {
      promises.push(Promise.resolve());
    }

    // Promise for getting comments. Comments have different connectivity logic to form link
    if (
      commentPermissions &&
      commentPermissions.view &&
      (commentPermissions.reply || commentPermissions.create)
    ) {
      const commentsPromise = new Promise((resolve, reject) => {
        jQuery.get(
          form_comment_get_path({
            format: "json",
            programme_form_link_id: this.getCommentTargetLinkId(form),
            entity_id: project_report_id,
            entity_type: "project_report",
          }),
          (data) => {
            this.comments[index] = data.comments;
            resolve();
          }
        ).fail((jqXHR, textStatus, errorThrown) => {
          console.error(`Failed to fetch comments: ${textStatus} - ${errorThrown}`);
          reject(new Error(`Failed to fetch comments: ${textStatus} - ${errorThrown}`));
        });
      });
      promises.push(commentsPromise);
    } else {
      promises.push(Promise.resolve());
    }

    return Promise.all(promises);
  };

  handleFormValueChange = (formIdx, answers) => {
    this.answers[formIdx] = answers;
    this.unsavedChanges = true;
  };

  handleTabClick = (form, index) => {
    const switchDisabled = window.fileUploader ? Object.values(window.fileUploader).includes("in_progress") : false;
    if(switchDisabled){
      alert(I18n.t('file_uploading_warning'))
      return
    }

    // If tab is already active, do nothing
    if (this.state.activeFormIndex == index) {
      return
    }

    this.setState({ formSubmitting: true, loading: true });

    if (!this.autosaveInProgress) {
      this.autosaveInProgress = true;
      // Save answers of currently open tab
      this.saveAnswersIfChanged().then((_) => {
        // Get new question data
        this.fetchQuestions(form, index).then((_) => {
          this.setState({
            activeFormIndex: index,
            formSubmitting: false,
            loading: false
          });
          this.autosaveInProgress = false;
          this.unsavedChanges = false;
        });
      });
    }
  };

  formAnswersCacheId = () => {
    // Unique ID for this collection of forms and answers
    return `project-report-forms-${this.props.project_report_id}-${this.props.forms
      .map((f) => f.id)
      .join("-")}`;
  };

  updateFormAnswersCache = (answers) => {
    window[this.formAnswersCacheId()] = answers;
  };

  readFormAnswersCache = () => {
    return window[this.formAnswersCacheId()];
  };

  componentWillUnmount() {
    if (this.props.editable) {
      // Store unsaved form answers over possible reloads that happen for example when
      // switching report versions.
      this.updateFormAnswersCache(this.unsavedChanges ? this.answers : null);
    }
  }

  componentDidMount() {
    const { asTabs, forms, editable, warnOnExit = false } = this.props;
    const { activeFormIndex } = this.state;
    this.unsavedChanges = !!Object.keys(this.props.prefill_answers || {}).length;

    this.setState({ loading: false });

    if (editable && warnOnExit) {
      AdaliaForm.updateOriginalState(this.formRef.current);
    }

    if (!asTabs) {
      Promise.all(
        forms.map((form, index) =>
          form.programme_form_link_id
            ? this.fetchQuestions(form, index)
            : Promise.resolve()
        )
      ).then(() => {
        this.setState({
          loadedFormIndexes: forms.map((_, idx) => idx),
        });
      });
    }

    // Add blur listener to submit and save buttons
    const submitButtons = document.querySelectorAll('.submit-button, .save-draft-button, .save-and-exit-button');
    submitButtons.forEach(button => {
      button.addEventListener('mouseenter', () => {
        if (document.activeElement && document.activeElement.blur) {
          document.activeElement.blur();
        }
      });
    });

    const updateErrorsObserver = () => {
      if (!this.props.survey_api_id) return;

      const errors = document.querySelector('.form-errors');
      if (errors === this.errorsElement) return;

      this.errorsResizeObserver?.disconnect();
      this.errorsElement = errors;

      if (errors) {
        this.errorsResizeObserver = new ResizeObserver(([entry]) => {
          const height = `${entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height}px`;
          document.documentElement.style.setProperty('--form-errors-height', height);
        });

        this.errorsResizeObserver.observe(errors);
      } else {
        document.documentElement.style.setProperty('--form-errors-height', '0px');
      }
    };

    this.errorsMutationObserver = new MutationObserver(updateErrorsObserver);

    this.errorsMutationObserver.observe(document.body, {childList: true, subtree: true});

    updateErrorsObserver();
  }

  componentDidUpdate(){
  }

  setDisableSubmit = (fieldName, disabled) => {
    if (this.state.disableSubmit[fieldName] !== disabled) {
      this.setState((prevState) => {
        return { disableSubmit: { ...prevState.disableSubmit, [fieldName]: disabled } };
      });
    }
  };

  setSelectedOptionalFormIds = (ids) => {
    this.setState({
      selectedOptionalFormIds: ids,
    });
  };

  setPrefillPreviewStatus = (newState) => {
    this.setState({
        isPreview: newState
    });
  }

  setPrefillAnswersFromMulti = (newState) => {
    this.setState({
        prefillAnswersFromMultiSource: newState
    });
  }

  setMultiSourceSelectionStatus = (newState) => {
    this.setState({
        showMultiSourceSelection: newState
    });
  }

  transformDataByNameKeyMap = (data) => {
    const reversedMap = {};
    const map = this.props.names_to_keys[this.currentProgrammeFormLinkId()]
    for (const key in map) {
        reversedMap[map[key]] = key;
    }
    const transformedData = {};

    for (const projectId in data) {
        for (const oldKey in data[projectId]) {
            const newKey = reversedMap[oldKey];
            transformedData[newKey] = data[projectId][oldKey];
        }
    }
    return transformedData
  }

  getExistingAnswersKeys = () => {
    const currentAnswer = this.answers[this.state.activeFormIndex]
    const map = this.props.names_to_keys[this.currentProgrammeFormLinkId()]
    const exist = Object.keys(currentAnswer)
    .filter(key => map[key] && currentAnswer[key] !== "" && !(Array.isArray(currentAnswer[key]) && currentAnswer[key].length === 0))
    .map(key => map[key])
    return exist
  }

  formatPrefillAnswers = (answers) => {
    let formattedAnswers = {}
    for (let id in answers) {
      formattedAnswers[id] = {};
        for (let key in answers[id]) {
          formattedAnswers[id][key] = [answers[id][key]];
        }
    }
    return formattedAnswers
  }

  render() {
    const { formSubmitting, disableSubmit, confirmation, savedSuccess, showMultiSourceSelection, isPreview } = this.state;
    const {
      editable,
      asTabs,
      saveText,
      saveAndExitText,
      submitText,
      actionButtons = [],
      project_report_id,
      report_phase_id,
      monitoring_report_id = null,
      project_id = null,
      compare_submissions = false,
      is_review,
      survey_api_id,
      monitoring_report_phase,
      btnPermissions = {}
    } = this.props;
    const submitDisabled = Object.values(disableSubmit).filter(Boolean).length > 0;
    const prefillContextFormLinks = monitoring_report_phase?.prefill_context_form_links;
    return (<PaginationBoundary>
    <PaginationContext.Consumer>
      {pagination => {
        // review has multiple forms. Shouldn't display navigation etc on bottom bar
        const pagedDisplay = pagination.pageCount != null && !this.props.is_review;
        const submitButton = typeof submitText == 'object'
          ? submitText.map((submitObject, idx) => (
              <button
                className={`btn btn-sm btn-${submitObject.buttonClass} submit-button mr-1  pull-right`}
                onClick={(e) =>
                  submitObject.save_draft_before_confirm
                    ? this.handleSave(e, () => this.confirmSubmit(submitObject.params))
                    : this.props.selectFollowingPhases
                      ? this.handleSubmit(e, submitObject.params)
                      : this.confirmSubmit(submitObject.params)
                }
                disabled={formSubmitting || submitDisabled}
                key={"review-submit-" + idx}
              >
                {submitObject.text}
              </button>
            ))
          : // Only show if submit_path defined (no permission if not)
            this.props.submit_path && (
              <button
                className='btn btn-sm btn-primary submit-button mr-1 pull-right'
                onClick={() => this.confirmSubmit()}
                disabled={formSubmitting || submitDisabled}
              >
                {submitText}
              </button>
            );


        const saveButtons = (this.props.save_path || this.props.is_review) && (
          <>
            {/* Hidden when already submitted, but allowed to edit anyway.*/}
            <button
              className={`btn btn-sm btn-default mr-1 save-draft-button pull-right`}
              onClick={this.handleSave}
              disabled={formSubmitting || submitDisabled}
            >
              {saveText}
            </button>

            {!this.props.is_review && (
              <button
                className="btn btn-sm btn-default mr-1 save-and-exit-button pull-right"
                onClick={this.handleSave}
                disabled={formSubmitting || submitDisabled}
                data-save-and-exit={true}
              >
                {saveAndExitText}
              </button>
            )}
          </>
        );

        const extraButtons = <>
          {btnPermissions.showCommentsBTN &&
            <a className='btn btn-sm btn-default' href={btnPermissions.showCommentsUrl}>
              <i className='fa fa-comment-o'/> {I18n.t('comments')}
            </a>
          }

          {btnPermissions.showPdfBTN &&
            <ProjectReportPdfExport {...{
              portalProps: btnPermissions.showPdfProps.portalProps,
              btnProps: {...btnPermissions.showPdfProps.btnProps, handleOnClick: (cb) => this.handlePdfBtnClick(cb)}}
            }/>
          }

          {btnPermissions.showLiveDiscussionBTN &&
            <DiscussionButton {...btnPermissions.showLiveDiscussionProps} btnSize='sm' dropup={!pagedDisplay} />
          }

          {(btnPermissions.showCommentsBTN || btnPermissions.showPdfBTN || btnPermissions.showLiveDiscussionBTN) && pagination.pageCount == null &&
            <div className='vertical-button-divider'/>
          }

          {!showMultiSourceSelection && isPreview &&
            <button
              className="btn btn-sm btn-default pull-left save-draft-button"
              onClick={() => this.setState({showMultiSourceSelection: true, prefillAnswersFromMultiSource: null})}
              style={{marginLeft: '60px'}}
            >
              {I18n.t('prefill_from_multi_modal_prefill_open_modal')}
            </button>
          }
        </>

        return <>
        {prefillContextFormLinks?.length > 0 && monitoring_report_phase?.body?.allow_select_prefill_source && showMultiSourceSelection ? (
          <PrefillSourceSelectionDialog
            prefillContextFormLinks={prefillContextFormLinks}
            setPrefillPreviewStatus={this.setPrefillPreviewStatus}
            setPrefillAnswersFromMulti={this.setPrefillAnswersFromMulti}
            setMultiSourceSelectionStatus={this.setMultiSourceSelectionStatus}
            existingAnswersKeys={this.getExistingAnswersKeys()}
           />
        ) : null
        }
        {confirmation.status == this.constructor.CONFIRMATION_STATUS.PENDING ? (
          <ConfirmDialog
            isReview={is_review}
            {...(confirmation.params || {})}
            projectReportId={project_report_id}
            reportPhaseId={report_phase_id}
            disabled={formSubmitting || submitDisabled}
            onClose={() =>
              this.setState((prevState) => ({
                confirmation: {
                  ...prevState.confirmation,
                  status: this.constructor.CONFIRMATION_STATUS.CANCELED,
                },
              }))
            }
            handleSubmit={(e) => this.handleSubmit(e, this.state.confirmation.params)}
          />
        ) : null}
        {
          savedSuccess && survey_api_id ? (
            <SaveSuccessDialog
              surveyApiId = {survey_api_id}
              onClose = {
                () => {
                  this.setState({
                    savedSuccess: false
                  })
                }
              }
            />
          ) : null
        }
        {asTabs ? this.renderAsTabs(this.formRef, extraButtons, pagedDisplay) : this.renderAsList(this.formRef)}

        {editable && (
          <div
            className={`form-collection-toolbar autosave-form-toolbar ${pagedDisplay ? 'paged-toolbar' : ''} ${survey_api_id ? 'survey-toolbar' : ''}`}
            style={{ padding: "0.5rem 1rem" }}
          >
          {/* PAGED FORM */}
          {pagedDisplay
          ? <div
              className={`toolbar-form-container ${this.props.is_review ? 'container-fluid mr-0 pr-0 pl-4' : `container pr-0 ${survey_api_id ? 'pl-0' : ''}`}`}
              style={survey_api_id ? {} : {paddingLeft: 320}}
            >
              { compare_submissions &&
                <div className='col-xs-12 col-md-6 pr-1 pl-3'>
                  <CompareSubmissionsButton {...{project_report_id, monitoring_report_id, project_id, previous_versions: this.props.previous_versions}} />
                </div>
              }
              <div className={`${this.props.is_review ? 'col-xs-12 col-md-6 p-0' : 'text-right'}`}>
                <div style={{display: 'flex', justifyContent: 'space-between', position: 'relative'}}>
                  <button
                    className='btn btn-sm btn-default ml-1'
                    disabled={pagination.shownPageIndex <= 0}
                    onClick={pagination.previousPage}
                    style={{zIndex: 2}}
                  >
                    <i className='fa fa-arrow-left'/> {I18n.t('back')}
                  </button>

                  <div className='bottom-bar-page-count' style={{display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'absolute', width: '100%', height: '100%', zIndex: 1}}>
                    {I18n.t('page_number_of_total', {number: pagination.shownPageIndex + 1, total: pagination.pageCount})}
                  </div>

                  <div style={{display: 'flex', alignItems: 'center', zIndex: 2}}>
                    {actionButtons.map(comp => {
                      const Component = comp.component
                      const {key, ...rest} = comp.props
                      return <Component saveFormDraft={this.handleSave} key={key} {...rest} />
                    })}
                    {saveButtons}
                    {pagination.shownPageIndex >= pagination.pageCount - 1
                      ? submitButton
                      : <button
                          className='btn btn-sm btn-primary mr-1'
                          disabled={pagination.shownPageIndex >= pagination.pageCount - 1}
                          onClick={pagination.nextPage}
                        >
                          <i className='fa fa-arrow-right'/> {I18n.t('next')}
                        </button>
                    }
                  </div>
                </div>
              </div>
            </div>
            // SINGLE-PAGE FORM
            :
            <div
              className={`${this.props.is_review ? 'container-fluid mr-0 pr-0 pl-4' : 'container pr-0'}`}
            >
              { compare_submissions &&
                <div className='col-xs-12 col-md-6 pr-1 pl-3'>
                  <CompareSubmissionsButton {...{project_report_id, monitoring_report_id, project_id, previous_versions: this.props.previous_versions}} />
                </div>
              }

              <div className={`${this.props.is_review ? 'col-xs-12 col-md-6 p-0' : 'text-right'}`}>
                {extraButtons}
                {submitButton}
                {saveButtons}
                {actionButtons.map(comp => {
                  const Component = comp.component
                  const {key, ...rest} = comp.props
                  return <Component saveFormDraft={this.handleSave} key={key} {...rest} />
                })}
              </div>
            </div>}
          </div>
        )}

        {survey_api_id && (<div id="comment-thread-wrapper"></div>)}
        </>
      }
    }</PaginationContext.Consumer>
    </PaginationBoundary>);
  }

  // Comments have different spaghetti logic of targetting the original linked form in reviews.
  // Return that if defined
  getCommentTargetLinkId = (form) => {
    const isNumeric = (num) => num != null && num !== '' && !isNaN(num);
    if (isNumeric(form.linked_programme_form_link_id)) {
      return form.linked_programme_form_link_id;
    }
    return form.programme_form_link_id;
  }

  renderAsTabs = (formRef, extraButtons, pagedDisplay) => {
    const { activeFormIndex, language, selectedOptionalFormIds, isPreview, prefillAnswersFromMultiSource } = this.state;
    const {
      forms,
      attachmentUploadUrl,
      attachmentDownloadParams = {},
      attachmentDownloadUrl = programme_form_download_attachment_path({
        programme_form_link_id: forms[activeFormIndex]?.programme_form_link_id || '__programme_form_link_id__',
        entity_id: this.props.project_report_id,
        entity_type: "project_report",
        attachment_attr: "__name__",
        project_report_version: this.props.project_report_version,
        ...attachmentDownloadParams
      }),
      editable,
      may_fill = false,
      show_pdf_button,
      commentSendUrl = "",
      commentEditUrl = "",
      commentPermissions,
      loggedPersonId,
      commentDeleteUrl = "",
      commentToggleThreadUrl = "",
      pdf,
      FormHeader,
      noFormsMessage,
      formWrapperClasses = "",
      showLanguageSelection = false,
      project_report_id,
      monitoring_report_id,
      monitoring_report_phase,
      warnOnExit = false,
      survey_api_id,
      monitoring_report_name
    } = this.props;

    if (forms.length == 0) {
      return <p>{noFormsMessage || I18n.t("no_forms")}</p>;
    }

    // Unique id for tab (or a part of the listing aka form). 
    //  However the backend seems to return different stuff for id, so can't be used for anything, except react key
    const uniqueKey = forms[this.state.activeFormIndex].id; 

    const programmeFormLinkId = forms[this.state.activeFormIndex].programme_form_link_id;
    const pdf_url = programme_form_pdf_path({
      programme_form_link_id: forms[activeFormIndex].programme_form_link_id,
      entity_id: project_report_id,
      entity_type: "project_report",
    });

    // BREAKING CHANGE [TRE]: Don't display form tabs when just single form connected to workflow.
    // Update 03.03: [Safe to Merge] This will be a bigger massive refactoring done later. This can be kept as is for now.
    const formTabs = forms.map((value, index) => {
            /* Show only optional forms chosen by Grantee */
            const isAssigned = value.status?.status === 'assigned';
            const isActive = index === activeFormIndex;
            const tabDisabled = this.state.loading && !isActive;
            return (value.required == false && !selectedOptionalFormIds.some(id => id == value.programme_form_link_id)) ? null : (
              <li
                key={index}
                className={isActive ? "selected" : ""}
                data-form-id={value.id}
                data-form-link-id={value.programme_form_link_id}
                onClick={() =>
                  this.handleTabClick(
                    value,
                    index,
                    forms[activeFormIndex].programme_form_link_id
                  )
                }
                style={tabDisabled
                  ? { cursor: "not-allowed", opacity: 0.4, pointerEvents: "none" }
                  : { cursor: "pointer" }
                }
              >
                {/* Display phase name (if present) and form name */}
                {`${value.body?.name ? `${value.body.name} - ` : ''}${getTranslation(value.name, language)}`}
                {editable && this.props.is_review && isAssigned &&  <span className={`label assigned ml-1`}>{I18n.t(`project_report_status_assigned`)}</span> }
                {this.formErrors[index] && Object.keys(this.formErrors[index]).length > 0 && (
                  <ErrorIndicator warningLevel={this.warningLevel} />
                )}
              </li>
            );
          }).filter((f) => f)

      const optionalFormTab = (forms.find(p => p.required == false) && editable && may_fill) &&
            <li className="pb-0">
              <OptionalFormsButton {...{project_report_id, monitoring_report_id, forms, selectedOptionalFormIds, setSelectedOptionalFormIds: this.setSelectedOptionalFormIds}}/>
            </li>

    const renderFormTabs = !!optionalFormTab || formTabs.length > 1;

    return (<FormWrapper editable={editable} title={monitoring_report_name} isSurvey={!!survey_api_id} form={this.questions[activeFormIndex]} extraButtons={extraButtons}>
      <form
        className={`adalia-form ${pagedDisplay ? 'paged-form' : ''}`}
        ref={formRef}
        style={{ paddingBottom: 60 }}
        name="programme_form"
        data-warn-on-exit={warnOnExit && editable}
      >
        {renderFormTabs && <ul className="project-title-tabs applicant form-collection-tabs">
       
          {formTabs}
          {optionalFormTab}

          {/*{showLanguageSelection ? (*/}
          {/*  <LanguageSelector*/}
          {/*    selected={language}*/}
          {/*    languages={this.questions[activeFormIndex]?.languages}*/}
          {/*    onChange={(l) => this.setState({ language: l })}*/}
          {/*    className="pull-right ml-0 pl-2 mr-0 pr-2"*/}
          {/*  />*/}
          {/*) : null}*/}
        </ul>
        }
        {FormHeader &&
          <FormHeader
            {...forms[activeFormIndex]}
            reportingRound={this.props.reporting_round}
            reviewFormHistory={this.props.review_form_history}
          />
        }
        {this.formErrors[activeFormIndex] &&
          Object.keys(this.formErrors[activeFormIndex]).length > 0 && (
            <FormErrors
              errors={this.formErrors[activeFormIndex]}
              title={I18n.t("active_validation_errors")}
              key={"errors-" + activeFormIndex}
              warningLevel={this.warningLevel}
              className={formWrapperClasses}
            />
          )}
        <div className={`report-form-wrapper ${formWrapperClasses}`}>
          {show_pdf_button && (
            <ul style={{ position: "absolute", zIndex: 10, right: "20px", marginTop: "10px" }}>
              <li className="pdf-export list-no-bullet">
                <PdfButton
                  btnProps={{ type: 'default', title: I18n.t("open_as_pdf")}}
                  portalProps={{
                    action: pdf_url,
                    enableDesign: false,
                    previewPath: pdf_url,
                  }}
                />
              </li>
            </ul>
          )}

          {this.state.loading ?
            <FormViewerSkeleton /> :
            <FormViewer
              answers={!isPreview
                ? monitoring_report_phase?.body?.allow_select_prefill_source ? ({...(typeof this.answers[activeFormIndex] == 'object' ? this.answers[activeFormIndex] : JSON.parse(this.answers[activeFormIndex] || '{}')), ...this.transformDataByNameKeyMap(prefillAnswersFromMultiSource)} || "{}" ) : this.answers[activeFormIndex]
                : this.transformDataByNameKeyMap(prefillAnswersFromMultiSource)}
              key={`form-${this.state.activeFormIndex}-${uniqueKey}-${language}`}
              language={language}
              availableLanguages={this.props.availableLanguages}
              form={this.questions[activeFormIndex]}
              formId={this.formIds[activeFormIndex]}
              optionSetValues={this.optionSetValues[activeFormIndex]}
              optionSetHierarchyValues={this.optionSetHierarchyValues[activeFormIndex]}
              programmeFormLinkId={programmeFormLinkId}
              editable={editable && !forms[activeFormIndex].disable_edit && !isPreview}
              valueChanged={(answers) => this.handleFormValueChange(activeFormIndex, answers)}
              commentChanged={(comments) => this.handleCommentChange(activeFormIndex, comments)}
              attachmentUploadUrl={attachmentUploadUrl}
              commentPermissions={commentPermissions}
              loggedPersonId={loggedPersonId}
              commentSendUrl={commentSendUrl.replace(
                "__programme_form_link_id__",
                this.getCommentTargetLinkId(forms[activeFormIndex])
              )}
              commentEditUrl={commentEditUrl.replace(
                "__programme_form_link_id__",
                this.getCommentTargetLinkId(forms[activeFormIndex])
              )}
              commentDeleteUrl={commentDeleteUrl.replace(
                "__programme_form_link_id__",
                this.getCommentTargetLinkId(forms[activeFormIndex])
              )}
              commentToggleThreadUrl={commentToggleThreadUrl.replace(
                "__programme_form_link_id__",
                this.getCommentTargetLinkId(forms[activeFormIndex])
              )}
              prefillAnswers={monitoring_report_phase?.body?.allow_select_prefill_source ? this.formatPrefillAnswers(prefillAnswersFromMultiSource) : this.props.prefill_answers}
              prefillAttachments={this.props.prefill_attachments}
              attachmentUploadId={this.props.attachmentUploadId}
              attachmentDownloadUrl={attachmentDownloadUrl.replace(
                "__programme_form_link_id__",
                forms[activeFormIndex].programme_form_link_id
              )}
              attachments={this.attachments[activeFormIndex] || "{}"}
              missingAttachments={this.props.missingAttachments}
              missingAttachmentsSaveUrl={this.props.missingAttachmentsSaveUrl?.replace(
                "__programme_form_link_id__",
                forms[activeFormIndex].programme_form_link_id
              )}
              saveAnswerBlobUrl={this.props.saveAnswerBlobUrl?.replace(
                "__programme_form_link_id__",
                forms[activeFormIndex].programme_form_link_id
              )}
              comments={this.comments[activeFormIndex] || "{}"}
              pdf={pdf}
              handleDisbursement={this.props.handleDisbursement}
              projects={this.props.projects}
              setDisableSubmit={this.setDisableSubmit}
              hideSensitiveInformation={this.props.hideSensitiveInformation}
              viewMode={this.props.viewMode}
              imagePermissions={this.props.imagePermissions}
              btnPermissions={this.props.btnPermissions}
              prefillFormProps={this.props.prefillFormProps || {}}
              lockFormFill={this.props.lock_form_fill}
              publicContext={this.props.publicContext}
              optionSetHierarchies={this.props.optionSetHierarchies}
              disablePagedMode={this.props.disablePagedMode}
            />
          }
        </div>
      </form>
    </FormWrapper>);
  };

  renderAsList = (formRef) => {
    const { activeFormIndex, loadedFormIndexes, language } = this.state;
    const {
      forms,
      attachmentUploadUrl,
      attachmentDownloadParams = {},
      editable,
      FormHeader,
      commentPermissions,
      pdf,
      warnOnExit = false
    } = this.props;

    const uniqueKey = forms.length > 0 && forms[this.state.activeFormIndex].id;
    if (forms.length == 0) {
      return <p>{I18n.t("no_forms")}</p>;
    }
    
    const {attachmentDownloadUrl =  programme_form_download_attachment_path({
        programme_form_link_id: "__programme_form_link_id__",
        entity_id: this.props.project_report_id,
        entity_type: "project_report",
        attachment_attr: "__name__",
        project_report_version: this.props.project_report_version,
        ...attachmentDownloadParams
      })} = this.props
    return (
      <>
        {forms.map((formDetails, idx) => {
          const programmeFormLinkId = forms[idx].programme_form_link_id
          const activeAndEditable = idx == activeFormIndex && editable;
          const [AnchorElement, anchorName] = PdfAnchor();
          return loadedFormIndexes.includes(idx) ? (
            <div key={`form-${idx}-${forms[idx].id}`}>
              {FormHeader ? (
                <FormHeader {...formDetails} />
              ) : (
                <h1 className="form-list-item-header title-underline mt-1" name={anchorName}>
                  {`${getTranslation(formDetails.name, I18n.locale)}${formDetails.version ? ` - v${formDetails.version}${formDetails.is_draft ? ` (${I18n.t("project_report_status_draft")})` : ""} ${formDetails.created_at}` : ""}`}
                </h1>
              )}
              <AnchorElement />
              <span className='running-pdf-title'> 
                { I18n.t('created_by_at', {
                  by: formDetails.created_by || I18n.t('unknown'),
                  at: `${DT.dt(formDetails.created_at, {datetime: true})}`
                })}
              </span>
              <ul className="project-title-tabs applicant" />
              <form
                className="adalia-form"
                ref={activeAndEditable ? formRef : null}
                name="programme_form"
                data-warn-on-exit={warnOnExit && activeAndEditable}
              >
                {this.formErrors[idx] && Object.keys(this.formErrors[idx]).length > 0 && (
                  <FormErrors
                    errors={this.formErrors[idx]}
                    title={I18n.t("active_validation_errors")}
                    key={"errors-" + idx}
                    warningLevel={this.warningLevel}
                  />
                )}
                {formDetails.hide_form ? null : this.questions[idx] ? (
                  <FormViewer
                    answers={this.answers[idx] || "{}"}
                    language={language}
                    availableLanguages={this.props.availableLanguages}
                    form={this.questions[idx]}
                    formId={this.formIds[idx]}
                    optionSetValues={this.optionSetValues[idx]}
                    optionSetHierarchyValues={this.optionSetHierarchyValues[idx]}
                    programmeFormLinkId={programmeFormLinkId}
                    key={`form-${idx}-${uniqueKey}`}
                    editable={activeAndEditable}
                    valueChanged={(answers) => this.handleFormValueChange(idx, answers)}
                    attachmentUploadId={this.props.attachmentUploadId}
                    attachmentUploadUrl={attachmentUploadUrl}
                    attachmentDownloadUrl={attachmentDownloadUrl.replace(
                      "__programme_form_link_id__",
                      programmeFormLinkId
                    )}
                    attachments={this.attachments[idx] || "{}"}
                    missingAttachments={this.props.missingAttachments}
                    missingAttachmentsSaveUrl={this.props.missingAttachmentsSaveUrl?.replace(
                      "__programme_form_link_id__",
                      programmeFormLinkId
                    )}
                    saveAnswerBlobUrl={this.props.saveAnswerBlobUrl?.replace(
                      "__programme_form_link_id__",
                      programmeFormLinkId
                    )}
                    comments={this.comments[idx] || "{}"}
                    prefillAnswers={this.props.prefill_answers}
                    lockFormFill={this.props.lock_form_fill}
                    prefillAttachments={this.props.prefill_attachments}
                    pdf={pdf}
                    handleDisbursement={this.props.handleDisbursement}
                    projects={this.props.projects}
                    setDisableSubmit={this.setDisableSubmit}
                    hideSensitiveInformation={this.props.hideSensitiveInformation}
                    viewMode={this.props.viewMode}
                    imagePermissions={this.props.imagePermissions}
                    prefillFormProps={this.props.prefillFormProps || {}}
                    publicContext={this.props.publicContext}
                    disablePagedMode={this.props.disablePagedMode}
                  />
                ) : (
                  <p className="text-danger mt-1 mb-1">{I18n.t("no_form_defined_for_phase")}</p>
                )}
              </form>

              {pdf && formDetails.comments ? (
                <FormComments
                  pdf={pdf}
                  comments={formDetails.comments || {}}
                  commentPermissions={commentPermissions}
                />
              ) : null}
            </div>
          ) : (
            <FormViewerSkeleton />
          );
        })}
      </>
    );
  };
}

FormCollection.defaultProps = {
  asTabs: true,
  submitText: I18n.t("submit"),
  saveText: I18n.t("save"),
  saveAndExitText: I18n.t("save_and_exit"),
  activeFormIndex: 0,
};
